import type { Address } from "viem"
import type { Config } from "wagmi"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { EventEmitter } from "@/domains/library"
import type { TransactionReturnType } from "@/domains/on-chain"
import { ManualClPositionBurnedEvent } from "@/domains/thick-v2/events"

import { burnThickNfpPosition } from "@/domains/on-chain/transactions/thick-v2/burn-thick-nfp-position"

/**
 * Burn (close) a thick-v2 position.
 *
 * Wraps the multicall encoder `burnThickNfpPosition` (decrease + collect +
 * burn) with toast emission and domain-event publication. Publishes
 * `ManualClPositionBurnedEvent` on success.
 */
export interface BurnThickNfpInteractionParams {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  poolAddress: Address
  /** Remaining liquidity in the position (used to decide whether to include
   * `decreaseLiquidity` in the multicall). */
  liquidity: bigint
  amount0Min: bigint
  amount1Min: bigint
  /** User-selected slippage. Transaction helper re-quotes live chain output before encoding. */
  slippagePercent: number
  deadline: number
  nfpManagerAddress: Address
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
}

export const burnThickNfpInteraction = async ({
  account,
  chainId,
  config,
  tokenId,
  poolAddress,
  liquidity,
  amount0Min,
  amount1Min,
  slippagePercent,
  deadline,
  nfpManagerAddress,
  emitter,
  eventBus,
}: BurnThickNfpInteractionParams): Promise<TransactionReturnType> => {
  const result = await burnThickNfpPosition({
    account,
    chainId,
    config,
    tokenId,
    liquidity,
    amount0Min,
    amount1Min,
    slippagePercent,
    deadline,
    nfpManagerAddress,
  })

  if (result.success) {
    emitter.emit("decrease-thick-nfp", {
      title: "Position closed",
      variant: "success",
    })
    eventBus.publish(
      new ManualClPositionBurnedEvent({
        poolAddress,
        tokenId,
        transactionHash: "tx" in result ? result.tx : undefined,
      }),
    )
    return result
  }

  emitter.emit("decrease-thick-nfp", {
    title: "Unable to close position",
    description: "error" in result ? result.error : undefined,
    variant: "error",
  })

  return result
}
